import { useState, type FormEvent } from "react";
import { Link, useParams } from "react-router-dom";

import AppShell from "../components/AppShell";
import {
  discoverPrinters,
  installPrinter,
  listDrivers,
  uploadDriverPackage,
  waitForTask,
  type FoundPrinter,
  type InstallRequest,
} from "../api/addPrinter";
import { useAuth } from "../stores/auth";

type Notice = { kind: "ok" | "error" | "busy"; text: string } | null;

/**
 * Add a printer to one office computer — found on the network, by address, or with a
 * driver package — without Windows' own "Add a printer" freezes: the computer does each
 * step in the background and this page shows how it went.
 */
export default function AddPrinter() {
  const { agentId = "" } = useParams<{ agentId: string }>();
  const role = useAuth((s) => s.role);
  const [notice, setNotice] = useState<Notice>(null);
  const [busy, setBusy] = useState(false);

  const [found, setFound] = useState<FoundPrinter[] | null>(null);
  const [drivers, setDrivers] = useState<string[] | null>(null);
  const [extraModels, setExtraModels] = useState<string[]>([]);

  const [address, setAddress] = useState("");
  const [name, setName] = useState("");
  const [mode, setMode] = useState<"driverless" | "driver">("driverless");
  const [driver, setDriver] = useState("");

  async function run<T>(busyText: string, fn: () => Promise<T>): Promise<T | null> {
    setBusy(true);
    setNotice({ kind: "busy", text: busyText });
    try {
      return await fn();
    } catch (e) {
      setNotice({ kind: "error", text: errorText(e) });
      return null;
    } finally {
      setBusy(false);
    }
  }

  async function onFind() {
    const task = await run("Looking for printers on this computer's network… (about 10–30 seconds)", async () =>
      waitForTask((await discoverPrinters(agentId)).task_id),
    );
    if (!task) return;
    if (task.status !== "succeeded") {
      setNotice({ kind: "error", text: task.error_message || "Couldn't look for printers." });
      return;
    }
    const list = task.result.printers ?? [];
    setFound(list);
    setNotice(list.length ? null : { kind: "error", text: "No printers answered. Check the printer is on and connected to the same network, or add it by its address below." });
  }

  async function onLoadDrivers() {
    const task = await run("Getting the printer drivers on this computer…", async () =>
      waitForTask((await listDrivers(agentId)).task_id),
    );
    if (!task) return;
    if (task.status === "succeeded") {
      setDrivers(task.result.drivers ?? []);
      setNotice(null);
    } else {
      setNotice({ kind: "error", text: task.error_message || "Couldn't list the drivers." });
    }
  }

  async function add(req: InstallRequest) {
    const task = await run(`Adding ${req.name}… (Windows may take up to a minute)`, async () =>
      waitForTask((await installPrinter(agentId, req)).task_id),
    );
    if (!task) return;
    if (task.status === "succeeded") {
      setNotice({
        kind: "ok",
        text: `Added ${req.name}. It appears in this computer's printers within a minute — share it under Sharing → Printers to use it on every computer.`,
      });
    } else {
      setNotice({ kind: "error", text: task.error_message || "Couldn't add the printer." });
    }
  }

  function addFound(p: FoundPrinter) {
    const printerName = cleanPrinterName(p.name || p.make_model || "", p.address);
    if (p.driverless && p.ipp_path) {
      void add({ address: p.address, mode: "driverless", ipp_path: p.ipp_path, name: printerName });
    } else {
      setAddress(p.address);
      setName(printerName);
      setMode("driver");
      if (!drivers) void onLoadDrivers();
      setNotice({ kind: "busy", text: "This printer needs its driver. Pick it below (or install the driver package first), then click Add printer." });
    }
  }

  function onManualAdd(e: FormEvent) {
    e.preventDefault();
    if (mode === "driverless") {
      void add({ address: address.trim(), mode: "driverless", ipp_path: "/ipp/print", name: name.trim() });
    } else {
      void add({ address: address.trim(), mode: "driver", driver, name: name.trim() });
    }
  }

  async function onDriverPackage(file: File) {
    const task = await run(`Installing ${file.name}… (this can take a few minutes)`, async () =>
      waitForTask((await uploadDriverPackage(agentId, file)).task_id),
    );
    if (!task) return;
    if (task.status === "succeeded") {
      const models = task.result.models ?? [];
      setExtraModels(models);
      setMode("driver");
      setNotice({
        kind: "ok",
        text: `Driver installed. A printer plugged in by USB now sets itself up within a minute. For a network printer, pick its model under "Use this driver" below.`,
      });
    } else {
      setNotice({ kind: "error", text: task.error_message || "Couldn't install the driver." });
    }
  }

  const driverChoices = Array.from(new Set([...(extraModels ?? []), ...(drivers ?? [])])).filter(
    (d) => !/^Microsoft (XPS|Print To PDF|Shared Fax|Software|Virtual|enhanced)/i.test(d),
  );

  return (
    <AppShell>
      <div className="mx-auto max-w-3xl">
        <Link to={`/computers/${agentId}`} className="text-sm text-slate-600 underline">
          ← Back to the computer
        </Link>
        <h1 className="mt-2 text-2xl font-semibold text-slate-900">Add a printer</h1>
        <p className="mt-1 text-slate-600">
          The computer does this in the background, so it never freezes like Windows&rsquo; own &ldquo;Add a printer&rdquo;.
          Printers plugged in by USB set themselves up — you only need this page for network printers or a missing driver.
        </p>

        {notice ? (
          <p
            role={notice.kind === "error" ? "alert" : "status"}
            className={`mt-4 rounded-lg p-3 text-sm ${
              notice.kind === "error"
                ? "bg-red-50 text-red-800"
                : notice.kind === "ok"
                  ? "bg-green-50 text-green-800"
                  : "bg-slate-100 text-slate-700"
            }`}
          >
            {notice.text}
          </p>
        ) : null}

        <section className="mt-6 rounded-2xl border border-slate-200 bg-white p-5">
          <h2 className="font-semibold text-slate-900">Find printers on the network</h2>
          <p className="mt-1 text-sm text-slate-600">Looks for printers connected by WiFi or cable to this computer&rsquo;s network.</p>
          <button
            type="button"
            onClick={() => void onFind()}
            disabled={busy}
            className="no-tap-highlight mt-3 rounded-lg bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800 disabled:opacity-50"
          >
            Find printers
          </button>
          {found && found.length > 0 ? (
            <ul className="mt-4 divide-y divide-slate-100">
              {found.map((p) => (
                <li key={p.address} className="flex flex-wrap items-center justify-between gap-3 py-3">
                  <div>
                    <p className="font-medium text-slate-900">{p.name || p.make_model || "Printer"}</p>
                    <p className="text-sm text-slate-600">
                      {p.address}
                      {p.make_model && p.make_model !== p.name ? ` · ${p.make_model}` : ""}
                      {p.driverless ? " · no driver needed" : ""}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => addFound(p)}
                    disabled={busy}
                    className="no-tap-highlight rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                  >
                    Add to this computer
                  </button>
                </li>
              ))}
            </ul>
          ) : null}
        </section>

        <section className="mt-6 rounded-2xl border border-slate-200 bg-white p-5">
          <h2 className="font-semibold text-slate-900">Add by address</h2>
          <form className="mt-3 grid gap-3 sm:grid-cols-2" onSubmit={onManualAdd}>
            <label className="flex flex-col text-sm text-slate-700">
              <span className="mb-1 font-medium">Printer&rsquo;s address</span>
              <input
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                placeholder="192.168.1.50"
                required
                className="rounded-lg border border-slate-300 px-3 py-2"
              />
            </label>
            <label className="flex flex-col text-sm text-slate-700">
              <span className="mb-1 font-medium">Name</span>
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Front desk printer"
                required
                maxLength={80}
                className="rounded-lg border border-slate-300 px-3 py-2"
              />
            </label>
            <fieldset className="text-sm text-slate-700 sm:col-span-2">
              <legend className="mb-1 font-medium">Driver</legend>
              <label className="mr-6 inline-flex items-center gap-2">
                <input type="radio" checked={mode === "driverless"} onChange={() => setMode("driverless")} />
                No driver needed (most printers from the last 8 years)
              </label>
              <label className="inline-flex items-center gap-2">
                <input
                  type="radio"
                  checked={mode === "driver"}
                  onChange={() => {
                    setMode("driver");
                    if (!drivers) void onLoadDrivers();
                  }}
                />
                Use this driver
              </label>
            </fieldset>
            {mode === "driver" ? (
              <label className="flex flex-col text-sm text-slate-700 sm:col-span-2">
                <span className="mb-1 font-medium">Driver on this computer</span>
                <select
                  value={driver}
                  onChange={(e) => setDriver(e.target.value)}
                  required
                  className="rounded-lg border border-slate-300 px-3 py-2"
                >
                  <option value="">{drivers || extraModels.length ? "Choose…" : "Loading…"}</option>
                  {driverChoices.map((d) => (
                    <option key={d} value={d}>
                      {d}
                    </option>
                  ))}
                </select>
              </label>
            ) : null}
            <div className="sm:col-span-2">
              <button
                type="submit"
                disabled={busy}
                className="no-tap-highlight rounded-lg bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800 disabled:opacity-50"
              >
                Add printer
              </button>
            </div>
          </form>
        </section>

        {role === "owner" ? (
          <section className="mt-6 rounded-2xl border border-slate-200 bg-white p-5">
            <h2 className="font-semibold text-slate-900">Install a printer driver</h2>
            <p className="mt-1 text-sm text-slate-600">
              For a printer this computer has no driver for (for example an ID card printer). Download the driver package
              from the printer maker&rsquo;s website — the .zip with the driver files, not the setup program — and choose it here.
            </p>
            <label className="no-tap-highlight mt-3 inline-block cursor-pointer rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50">
              Choose driver package (.zip)
              <input
                type="file"
                accept=".zip"
                className="hidden"
                disabled={busy}
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  e.target.value = "";
                  if (f) void onDriverPackage(f);
                }}
              />
            </label>
          </section>
        ) : null}
      </div>
    </AppShell>
  );
}

function errorText(e: unknown): string {
  if (typeof e === "object" && e && "response" in e) {
    const response = (e as { response?: { data?: { detail?: string } } }).response;
    if (response?.data?.detail) return response.data.detail;
  }
  if (e instanceof Error && e.message) return e.message;
  return "Something went wrong. Please try again.";
}

// Printers report names like "HP Color LaserJet MFP M479fdw [D2E3F4]": keep letters, numbers,
// spaces and simple punctuation (what Windows printer names here allow), at most 80 characters.
function cleanPrinterName(raw: string, address: string): string {
  const cleaned = raw
    .replace(/[^A-Za-z0-9 ._()\-+#&]/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, 80)
    .trim();
  return cleaned || `Printer ${address}`;
}
